import { useState, type FormEvent } from 'react';
import { ErrorState, Loading } from '../../components/States';
import { useAdminProducts, useCreateAdminProduct, useUpdateAdminProduct, useUploadAdminProductThumbnail } from '../../hooks/api-hooks';
import { formatDateTime, formatPrice } from '../../lib/format';
import { resolveMediaUrl } from '../../lib/media-url';
import type { ProductType } from '../../lib/types';
import { AdminCourseContentPanel } from './AdminCourseContentPanel';
import { AdminSkillContentPanel } from './AdminSkillContentPanel';

const slugify = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/đ/g, 'd')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

export function AdminProductsPage() {
  const [type, setType] = useState<ProductType>('COURSE');
  const products = useAdminProducts(type);
  const create = useCreateAdminProduct();
  const update = useUpdateAdminProduct();
  const uploadCover = useUploadAdminProductThumbnail();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [price, setPrice] = useState('');
  const [level, setLevel] = useState('Cơ bản');
  const [estimatedMinutes, setEstimatedMinutes] = useState('');
  const [publishNow, setPublishNow] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string>();
  const selectedProduct = products.data?.find((product) => product.id === selectedProductId);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate({
      type,
      title: title.trim(),
      slug: slug.trim(),
      shortDescription: shortDescription.trim() || undefined,
      price: Number(price),
      status: publishNow ? 'PUBLISHED' : 'DRAFT',
      ...(type === 'COURSE' ? {
        level: level.trim() || undefined,
        estimatedMinutes: estimatedMinutes ? Number(estimatedMinutes) : undefined,
      } : {}),
    }, {
      onSuccess: (product) => {
        if (coverFile) uploadCover.mutate({ id: product.id, file: coverFile });
        setTitle('');
        setSlug('');
        setShortDescription('');
        setPrice('');
        setEstimatedMinutes('');
        setPublishNow(false);
        setCoverFile(null);
      },
    });
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <div><h1>Khóa học & skill</h1><p>Tạo sản phẩm và tải ảnh bìa, video, file skill trực tiếp từ trang quản trị.</p></div>
      </header>

      <div className="admin-tabs" role="tablist" aria-label="Loại sản phẩm">
        <button type="button" className={type === 'COURSE' ? 'active' : ''} onClick={() => { setType('COURSE'); setSelectedProductId(undefined); }}>Khóa học</button>
        <button type="button" className={type === 'SKILL' ? 'active' : ''} onClick={() => { setType('SKILL'); setSelectedProductId(undefined); }}>Skill</button>
      </div>

      <details className="admin-create-panel">
        <summary>Thêm {type === 'COURSE' ? 'khóa học' : 'skill'} mới</summary>
        <form className="admin-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <label>Tên sản phẩm<input required value={title} onChange={(event) => { setTitle(event.target.value); if (!slug) setSlug(slugify(event.target.value)); }} /></label>
            <label>Đường dẫn<input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={slug} onChange={(event) => setSlug(slugify(event.target.value))} /></label>
            <label className="admin-form-wide">Mô tả ngắn<textarea value={shortDescription} onChange={(event) => setShortDescription(event.target.value)} /></label>
            <label>Giá bán (VND)<input required min="0" step="1000" type="number" value={price} onChange={(event) => setPrice(event.target.value)} /></label>
            <label>Ảnh bìa trên R2<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={(event) => setCoverFile(event.target.files?.[0] ?? null)} /></label>
            {type === 'COURSE' && <>
              <label>Cấp độ<input value={level} onChange={(event) => setLevel(event.target.value)} /></label>
              <label>Thời lượng dự kiến (phút)<input min="0" type="number" value={estimatedMinutes} onChange={(event) => setEstimatedMinutes(event.target.value)} /></label>
            </>}
            <label className="admin-checkbox"><input type="checkbox" checked={publishNow} onChange={(event) => setPublishNow(event.target.checked)} /> Đăng bán ngay</label>
          </div>
          {create.error && <ErrorState error={create.error} />}
          <button disabled={create.isPending || uploadCover.isPending}>{create.isPending || uploadCover.isPending ? 'Đang lưu…' : 'Tạo sản phẩm'}</button>
        </form>
      </details>

      {products.isPending && <Loading label="Đang tải sản phẩm…" />}
      {products.error && <ErrorState error={products.error} />}
      {products.data && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Ảnh bìa R2</th><th>Sản phẩm</th><th>Giá</th><th>Nội dung</th><th>Ngày tạo</th><th>Trạng thái</th><th></th></tr></thead>
            <tbody>
              {products.data.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="admin-cover-cell">
                      {product.thumbnailUrl ? <img src={resolveMediaUrl(product.thumbnailUrl)} alt="" /> : <span>Chưa có ảnh</span>}
                      <label className="file-button">
                        {uploadCover.isPending && uploadCover.variables?.id === product.id ? 'Đang tải…' : 'Tải ảnh'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/avif"
                          disabled={uploadCover.isPending}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) uploadCover.mutate({ id: product.id, file });
                          }}
                        />
                      </label>
                    </div>
                  </td>
                  <td><strong>{product.title}</strong><small>/{product.slug}</small></td>
                  <td>{formatPrice(Number(product.price))}</td>
                  <td>{type === 'COURSE' ? `${product.lessonCount} bài học` : `${product.versionCount} phiên bản`}</td>
                  <td>{formatDateTime(product.createdAt)}</td>
                  <td>
                    {product.status === 'ARCHIVED' ? <span className="status-badge muted-badge">Đã lưu trữ</span> : (
                      <button
                        type="button"
                        className={`status-button ${product.status === 'PUBLISHED' ? 'published' : ''}`}
                        disabled={update.isPending}
                        onClick={() => update.mutate({ id: product.id, input: { status: product.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED' } })}
                      >{product.status === 'PUBLISHED' ? 'Đang bán' : 'Bản nháp'}</button>
                    )}
                  </td>
                  <td><button type="button" className="btn btn-small btn-quiet" onClick={() => setSelectedProductId(product.id)}>{product.type === 'COURSE' ? 'Chương, bài & video' : 'File skill trên R2'}</button></td>
                </tr>
              ))}
              {!products.data.length && <tr><td colSpan={7} className="admin-empty">Chưa có sản phẩm.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {uploadCover.error && <ErrorState error={uploadCover.error} />}
      {uploadCover.isSuccess && <p className="success" role="status">Ảnh bìa đã được lưu lên R2.</p>}
      {selectedProduct?.type === 'COURSE' && selectedProduct.courseId && (
        <AdminCourseContentPanel courseId={selectedProduct.courseId} onClose={() => setSelectedProductId(undefined)} />
      )}
      {selectedProduct?.type === 'SKILL' && selectedProduct.skillId && (
        <AdminSkillContentPanel skillId={selectedProduct.skillId} title={selectedProduct.title} onClose={() => setSelectedProductId(undefined)} />
      )}
    </section>
  );
}
